import { Policy, RuleModel } from '../../../../types';
import { actionOptions } from './action-options';
import SharedActionAccordion from '../../../shared/shared-action-accordion';
import HtmlPreview from '../../../shared/html-preview';
import RulesEngineGroupTemplateDescription from './rules-engine-group-template-description';
import { RulesEngineForm } from './rules-engine-form-page';
import EmptyStateMessage from '../../../shared/empty-state-message';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import { formatStringList } from '../../automation/decommissioning/decom-policy-form/decom-policy-utils';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  getAllResourceCalendars,
  getCustomSchemas,
  getDocById,
  getSharedDrivesByIdsQuery,
  UserKeys,
} from '../../../api/users-api';
import { useCallback, useMemo, useState } from 'react';
import {
  employeeInfoFields,
  profileFieldOptions,
} from '../../../shared/shared-profile-field-filter';
import AvatarChip from '../../../shared/avatar-chip';
import { documentTypeOptions } from '../../../shared/google-document-types';
import {
  userSharingRadioOptions,
  domainSharingRadioOptions,
} from './sharing-options';
import {
  triggerEventOptions,
  triggerGroupUiOptions,
  getEventUiGroup,
} from './trigger-event-options';

import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { capitalize } from '@mui/material/utils';

interface RulesDetailsSummaryOldProps {
  selectedRule: RuleModel;
  allPolicies: Policy[] | undefined;
  reviewStepText?: string;
}

export default function RulesDetailsSummaryOld({
  selectedRule,
  allPolicies,
  reviewStepText,
}: RulesDetailsSummaryOldProps) {
  const highlight = (text: string) => (
    <Box component="span" sx={{ fontWeight: 'bold' }}>
      {text}
    </Box>
  );
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    new Set()
  );

  const toggleAccordion = useCallback((id: string) => {
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  function createDetailSummary(action: string) {
    const { actionInfo, emailInfo, chatInfo, policyInfo } = selectedRule;
    switch (action) {
      case 'sendEmailTemplate':
      case 'sendEmail': {
        const emailNotificationEmails: string[] = Array.isArray(
          emailInfo?.emailNotificationEmails
        )
          ? emailInfo.emailNotificationEmails.map((target) => {
              if (target.includes(':')) {
                return target.substring(target.lastIndexOf(':') + 1);
              }
              return target;
            })
          : [];
        const emailString = formatStringList(emailNotificationEmails);
        return (
          <>
            <Stack
              direction="column"
              spacing={1}
              flexWrap="wrap"
              sx={{ lineHeight: 2 }}
            >
              <Typography
                component="span"
                variant="body2"
                sx={{ lineHeight: 2, wordBreak: 'break-word' }}
              >
                When this rule is triggered,{' '}
                {emailString ? highlight(emailString) : 'N/A'} will be notified.
              </Typography>
              {(emailInfo?.ccEmails?.length ?? 0) > 0 && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ lineHeight: 2, wordBreak: 'break-word' }}
                >
                  The following users will be CC'd:{' '}
                  {highlight(formatStringList(emailInfo.ccEmails) || 'N/A')}
                </Typography>
              )}
              {(emailInfo?.bccEmails?.length ?? 0) > 0 && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ lineHeight: 2, wordBreak: 'break-word' }}
                >
                  The following users will be BCC'd:{' '}
                  {highlight(formatStringList(emailInfo.bccEmails) || 'N/A')}
                </Typography>
              )}
            </Stack>
          </>
        );
      }
      case 'runPolicy': {
        const policyName = allPolicies?.find(
          (policy) => policy.id === policyInfo?.policyId
        )?.policyName;
        return (
          <>
            {highlight(policyName || 'N/A')} will be run when this rule is
            triggered.
          </>
        );
      }
      case 'changeCalendarTimezone':
        return (
          <>
            The user's calendar's timezone will be changed to{' '}
            {highlight(actionInfo?.timeZone || 'N/A')} when this rule is
            triggered.
          </>
        );
      case 'sendChatMessage': {
        return (
          <>
            <Typography component="span" variant="body2" sx={{ lineHeight: 2 }}>
              This will send a message to{' '}
              {highlight(
                (chatInfo?.chatUrl || 'N/A').replace(/<p>(.*?)<\/p>/, '$1')
              )}{' '}
              when rule is triggered.
            </Typography>
          </>
        );
      }
      case 'addToGroup':
        return (
          <>
            <Typography component="span" variant="body2" sx={{ lineHeight: 2 }}>
              When this rule is triggered, the user will be added to the
              following groups:
            </Typography>
          </>
        );
      case 'removeFromGroup':
        return (
          <>
            <Typography component="span" variant="body2" sx={{ lineHeight: 2 }}>
              When this rule is triggered, the user will be removed from the
              following groups:
            </Typography>
          </>
        );
      case 'limitOccurrences':
        return (
          <>
            <Typography component="span" variant="body2" sx={{ lineHeight: 2 }}>
              Limit the number of event occurrences on calendars by{' '}
              {highlight(
                selectedRule.actionInfo?.occurrenceLimit?.toString() || 'N/A'
              )}
              .
            </Typography>
          </>
        );
      case 'applyGroupTemplate': {
        return (
          <RulesEngineGroupTemplateDescription
            selectedRule={selectedRule as RulesEngineForm}
          />
        );
      }
      default:
        return actionOptions.find((x) => x.value === action)?.description;
    }
  }

  function createDetailBody(action: string) {
    const { emailInfo, chatInfo } = selectedRule;
    const { groupInfo, removeGroupInfo } = selectedRule as RulesEngineForm;

    switch (action) {
      case 'sendEmailTemplate':
      case 'sendEmail': {
        return (
          // Body part
          <Stack direction="column" sx={{ mt: 2 }} spacing={1}>
            <Typography component="span" variant="body2">
              {highlight('Subject:')}{' '}
              {(emailInfo?.emailSubject ?? 'N/A')
                .replace(/&nbsp;/gi, ' ')
                .trim()}
            </Typography>
            <HtmlPreview
              title="Email Preview"
              html={
                (emailInfo?.emailMessage ?? 'N/A')
                  .replace(/&nbsp;/gi, ' ')
                  .trim() || 'N/A'
              }
              width={556}
            />
          </Stack>
        );
      }
      case 'sendChatMessage': {
        return (
          <HtmlPreview
            title="Chat Message Preview"
            html={
              (chatInfo?.chatMessage || 'N/A')
                .replace(/(<([^>]+)>)/gi, '')
                .replace(/&nbsp;/gi, ' ')
                .trim() || ' '
            }
            width={556}
          />
        );
      }
      case 'addToGroup':
        return (
          <>
            <TableContainer component={Paper} sx={{ mt: 1, maxWidth: 556 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Group Email</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(groupInfo?.groupEmail || []).map((email, index) => (
                    <TableRow key={`${email}-${index}`}>
                      <TableCell>{email}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        {groupInfo?.groupRole?.[index]?.toLowerCase() || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      case 'removeFromGroup':
        return (
          <>
            <TableContainer component={Paper} sx={{ mt: 1, maxWidth: 556 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Group Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(removeGroupInfo?.groupEmail || []).map((email, index) => (
                    <TableRow key={`${email}-${index}`}>
                      <TableCell>{email}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      default:
        return null; // No body for simple actions
    }
  }

  const { data: customSchemas } = useQuery({
    queryKey: ['customSchemas'],
    queryFn: getCustomSchemas,
  });

  const sharedDriveIds = selectedRule.triggerInfo.sharedDriveIds || [];
  const { data: sharedDrives, isLoading: isLoadingSharedDrives } = useQuery({
    ...getSharedDrivesByIdsQuery(sharedDriveIds),
    enabled: sharedDriveIds.length > 0,
  });

  const resolveProfileFieldDisplay = useCallback(
    (profileFieldType: string): string => {
      const empField = employeeInfoFields.find(
        (f) => f.value === profileFieldType
      );
      if (empField) return empField.display;

      const primaryOrgField = profileFieldOptions.find(
        (f) => f.value === profileFieldType
      );
      if (primaryOrgField) return primaryOrgField.display;

      if (profileFieldType.startsWith('customAttribute.')) {
        // Look up in customSchemas to get the actual displayName
        if (customSchemas) {
          for (const schema of customSchemas) {
            const field = schema.fields?.find(
              (f) =>
                `customAttribute.${schema.schemaName}.${f.fieldName}` ===
                profileFieldType
            );
            if (field) {
              return `${schema.displayName} - ${field.displayName}`;
            }
          }
        }

        // Fallback if schema not found
        const parts = profileFieldType.split('.');
        if (parts.length >= 3) {
          const category = parts[1];
          const fieldName = parts.slice(2).join('.');
          return `${category} - ${fieldName}`;
        }
        return profileFieldType;
      }
      return profileFieldType;
    },
    [customSchemas]
  );

  const { data: folderData, pending: isLoadingFolderData } = useQueries({
    queries: (selectedRule.triggerInfo.driveFolderTriggerSources || [])
      .filter((s) => s.driveFolderId && s.driveFolderOwner)
      .map((source) => ({
        queryKey: [
          UserKeys.DriveFolder,
          source.driveFolderId,
          source.driveFolderOwner,
        ],
        queryFn: () =>
          getDocById(source.driveFolderOwner, source.driveFolderId),
        staleTime: 1000 * 60 * 5,
      })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const { data: resourceCalendars, isLoading: isLoadingCalendars } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
  });

  const calendarEmailToNameMap = useMemo(() => {
    if (!resourceCalendars) return new Map<string, string>();
    return new Map(
      resourceCalendars.map((c) => [c.resourceEmail, c.resourceName])
    );
  }, [resourceCalendars]);

  const basicInfoData: Record<string, React.ReactNode> = useMemo(() => {
    const getOccurrences = (): string => {
      if (selectedRule.triggerInfo.occursEverytime) {
        return 'Occurs every time';
      }
      let timeString = ` ${selectedRule.triggerInfo.triggerThreshold} or more times`;
      if (selectedRule.triggerInfo.hasTimeDuration) {
        timeString += ` within ${selectedRule.triggerInfo.triggerDuration} ${selectedRule.triggerInfo.triggerDurationType}`;
      }
      return timeString;
    };

    const sourceTypes: string[] = [];
    if (selectedRule.triggerInfo.triggerUserSources?.length)
      sourceTypes.push('User');
    if (selectedRule.triggerInfo.triggerGroupSources?.length)
      sourceTypes.push('Group');
    if (selectedRule.triggerInfo.triggerOrgSources?.length)
      sourceTypes.push('OrgUnit');
    if (selectedRule.triggerInfo.triggerDomainSources?.length)
      sourceTypes.push('Domain');

    const sourceTypesString = sourceTypes.join(', ');

    const allSources = [
      ...(selectedRule.triggerInfo.triggerUserSources || []).map((source) => ({
        label: source,
        type: 'User' as const,
      })),
      ...(selectedRule.triggerInfo.triggerGroupSources || []).map((source) => ({
        label: source,
        type: 'Group' as const,
      })),
      ...(selectedRule.triggerInfo.triggerOrgSources || []).map((source) => ({
        label: source,
        type: 'OrgUnit' as const,
      })),
      ...(selectedRule.triggerInfo.triggerDomainSources || []).map(
        (source) => ({
          label: source,
          type: 'Domain' as const,
        })
      ),
      // Drive Sources
      ...(isLoadingCalendars
        ? (selectedRule.triggerInfo.triggerCalendarSources || []).map(
            (source) => ({
              label: <Skeleton width={100} />,
              type: 'Resource Calendar' as const,
              key: `cal-skeleton-${source}`,
            })
          )
        : (selectedRule.triggerInfo.triggerCalendarSources || []).map(
            (source) => ({
              label: calendarEmailToNameMap.get(source) ?? source,
              type: 'Resource Calendar' as const,
            })
          )),
      ...(isLoadingSharedDrives
        ? (selectedRule.triggerInfo.sharedDriveIds || []).map((id) => ({
            label: <Skeleton width={100} />,
            type: 'Shared Drive' as const,
            key: `sd-skeleton-${id}`,
          }))
        : (selectedRule.triggerInfo.sharedDriveIds || []).map((id) => {
            const driveName =
              sharedDrives?.find((drive) => drive.id === id)?.name || id;
            return {
              label: driveName,
              type: 'Shared Drive' as const,
            };
          })),
      ...(selectedRule.triggerInfo.driveRegExs || []).map((source) => ({
        label: source,
        type: 'RegEx' as const,
      })),
      ...(isLoadingFolderData
        ? (selectedRule.triggerInfo.driveFolderTriggerSources || [])
            .filter((s) => s.driveFolderId && s.driveFolderOwner)
            .map((source) => ({
              label: <Skeleton width={150} />,
              type: 'User Drive' as const,
              key: `ud-skeleton-${source.driveFolderId}`,
            }))
        : (selectedRule.triggerInfo.driveFolderTriggerSources || [])
            .filter((s) => s.driveFolderId && s.driveFolderOwner)
            .map((source, index) => {
              const folderName =
                folderData?.[index]?.title || source.driveFolderId;
              return {
                label: `${source.driveFolderOwner}: ${folderName}`,
                type: 'User Drive' as const,
              };
            })),
    ];

    return {
      'Created By': selectedRule.basicInfo.creator ? (
        <AvatarChip
          email={selectedRule.basicInfo.creator}
          type="User"
          label={selectedRule.basicInfo.creator}
        />
      ) : (
        'N/A'
      ),
      triggerGroup:
        triggerGroupUiOptions.find(
          (o) =>
            o.value === getEventUiGroup(selectedRule.triggerInfo.triggerEvent)
        )?.displayValue ||
        capitalize(selectedRule.triggerInfo.triggerGroup) ||
        '',
      triggerAction:
        triggerEventOptions.find(
          (o) => o.value === selectedRule.triggerInfo.triggerEvent
        )?.displayValue || selectedRule.triggerInfo.triggerEvent,
      occurrences: getOccurrences(),
      sourceType: sourceTypesString,
      'Source(s)': selectedRule.allSource ? (
        <Stack>
          <Typography variant="body2">
            All users
            {selectedRule.targetsExcluded?.length
              ? ' except for the following:'
              : ''}
          </Typography>
          {selectedRule.targetsExcluded?.length ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ pt: 0.5 }}>
              {selectedRule.targetsExcluded.map((user) => (
                <AvatarChip key={user} email={user} type="User" />
              ))}
            </Stack>
          ) : null}
        </Stack>
      ) : allSources.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {allSources.map((source) =>
            'key' in source ? (
              source.label
            ) : (
              <AvatarChip
                key={`${source.type}-${source.label}`}
                label={source.label as string}
                email={source.label as string}
                type={source.type}
              />
            )
          )}
        </Stack>
      ) : (
        'N/A'
      ),
    };
  }, [
    selectedRule,
    sharedDrives,
    folderData,
    isLoadingSharedDrives,
    isLoadingFolderData,
    calendarEmailToNameMap,
    isLoadingCalendars,
  ]);

  const adminFiltersData: Record<string, React.ReactNode> | null =
    useMemo(() => {
      const ruleWithAdminFilters = selectedRule as RuleModel & {
        profileFieldFilter?: {
          profileFieldType?: string;
          profileFieldValue?: string;
          profileFieldValues?: Array<{ value: string }>;
          profileFieldValueExact?: boolean;
          profileFieldRegex?: string;
        };
        hasProfileFieldFilter?: boolean;
      };
      const { profileFieldFilter, hasProfileFieldFilter } =
        ruleWithAdminFilters;

      // Helper function to get profile field values display
      const getProfileFieldValuesDisplay = (filter: any): string => {
        if (Array.isArray(filter?.profileFieldValues)) {
          const values = filter.profileFieldValues.map((v: any) =>
            typeof v === 'string' ? v : v?.value
          );
          if (values.length > 0) {
            return values.join(', ');
          }
        }
        return filter?.profileFieldValue || 'N/A';
      };

      if (
        !(
          (hasProfileFieldFilter === true ||
            hasProfileFieldFilter === undefined) &&
          (profileFieldFilter?.profileFieldType ||
            profileFieldFilter?.profileFieldValue ||
            profileFieldFilter?.profileFieldValues?.some((v: any) =>
              Boolean(typeof v === 'string' ? v : v?.value)
            ) ||
            profileFieldFilter?.profileFieldRegex)
        )
      ) {
        return null;
      }

      return {
        profileField: profileFieldFilter?.profileFieldType
          ? resolveProfileFieldDisplay(profileFieldFilter.profileFieldType)
          : 'N/A',
        profileFieldValue: getProfileFieldValuesDisplay(profileFieldFilter),
        exactMatch: profileFieldFilter?.profileFieldValueExact ? 'Yes' : 'No',
        regularExpression:
          (profileFieldFilter?.profileFieldRegex as React.ReactNode) || 'N/A',
      };
    }, [selectedRule, resolveProfileFieldDisplay]);

  const driveFiltersData: Record<string, React.ReactNode> | null =
    useMemo(() => {
      const {
        driveFileSizeFloor,
        driveFileSizeCeiling,
        fileMimeTypes,
        fileIsSharedPublic,
        fileIsSharedToDomain,
        fileSharedToUserExternalToDomain,
        fileSharedToUserInternalToDomain,
        fileSharedToSpecificUser,
        canFindInSearchResults,
        mustHaveLinkToAccess,
      } = selectedRule.triggerInfo;

      const getFileSizeFilter = (): string | null => {
        if (!driveFileSizeFloor && !driveFileSizeCeiling) {
          return null;
        }

        const sizeComparison =
          driveFileSizeFloor > 0 && driveFileSizeCeiling > 0
            ? 'Between'
            : driveFileSizeFloor > 0
            ? 'Greater Than'
            : 'Less Than';

        let displayFloor = driveFileSizeFloor;
        let displayCeiling = driveFileSizeCeiling;
        let unit = 'bytes';

        const floorInKB = driveFileSizeFloor / 1024;
        const ceilingInKB = driveFileSizeCeiling / 1024;
        const floorInMB = floorInKB / 1024;
        const ceilingInMB = ceilingInKB / 1024;

        if (floorInMB >= 1 || ceilingInMB >= 1) {
          displayFloor = floorInMB;
          displayCeiling = ceilingInMB;
          unit = 'MB';
        } else if (floorInKB >= 1 || ceilingInKB >= 1) {
          displayFloor = floorInKB;
          displayCeiling = ceilingInKB;
          unit = 'KB';
        }

        switch (sizeComparison) {
          case 'Greater Than':
            return `${sizeComparison} ${displayFloor} ${unit}`;
          case 'Less Than':
            return `${sizeComparison} ${displayCeiling} ${unit}`;
          case 'Between':
            return `${capitalize(
              sizeComparison
            )} ${displayFloor} and ${displayCeiling} ${unit}`;
          default:
            return null;
        }
      };

      const getFileSharingFilter = () => {
        const sharingOptions = [];

        if (fileIsSharedPublic)
          sharingOptions.push(
            userSharingRadioOptions.find(
              (o) => o.value === 'fileIsSharedPublic'
            )?.displayValue
          );
        if (fileSharedToUserExternalToDomain)
          sharingOptions.push(
            userSharingRadioOptions.find(
              (o) => o.value === 'fileSharedToUserExternalToDomain'
            )?.displayValue
          );
        if (fileSharedToUserInternalToDomain)
          sharingOptions.push(
            userSharingRadioOptions.find(
              (o) => o.value === 'fileSharedToUserInternalToDomain'
            )?.displayValue
          );
        if (fileIsSharedToDomain) {
          const base = userSharingRadioOptions.find(
            (o) => o.value === 'fileIsSharedToDomain'
          )?.displayValue;
          let specific = '';
          if (canFindInSearchResults) {
            specific =
              domainSharingRadioOptions.find(
                (o) => o.value === 'canFindInSearchResults'
              )?.displayValue || '';
          } else if (mustHaveLinkToAccess) {
            specific =
              domainSharingRadioOptions.find(
                (o) => o.value === 'mustHaveLinkToAccess'
              )?.displayValue || '';
          }
          sharingOptions.push(specific ? `${base} (${specific})` : base);
        }

        const specificUsers = (fileSharedToSpecificUser || []).map((user) => (
          <AvatarChip key={user} email={user} type="User" size="small" />
        ));

        const allFilters = [
          ...sharingOptions.filter(Boolean),
          ...specificUsers,
        ];

        return allFilters.length > 0 ? allFilters : null;
      };

      if (
        !getFileSizeFilter() &&
        !fileMimeTypes?.length &&
        !getFileSharingFilter()
      ) {
        return null;
      }

      return {
        ...(fileMimeTypes?.length && {
          fileTypeFilter: (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {fileMimeTypes.map((mimeType) => {
                const docType = documentTypeOptions.find(
                  (o) => o.value === mimeType
                );
                return (
                  <Chip
                    key={mimeType}
                    label={docType?.displayValue || mimeType}
                    avatar={<Avatar src={docType?.icon} />}
                    size="small"
                  />
                );
              })}
            </Stack>
          ),
        }),
        ...(getFileSizeFilter() && {
          fileSize: getFileSizeFilter(),
        }),
        ...(getFileSharingFilter() && {
          fileSharingFilter: getFileSharingFilter(),
        }),
      };
    }, [selectedRule]);

  const groupFiltersData: Record<string, React.ReactNode> | null =
    useMemo(() => {
      const groupFilter = selectedRule.groupFilter;
      if (!groupFilter) return null;

      const {
        hasGroupNameFilter,
        groupNameMode,
        groupName,
        groupNameExact,
        groupNameRegex,
        hasMemberCountFilter,
        memberCountMode,
        memberCountOperator,
        memberCount,
        memberCountRangeLessThan,
        memberCountRangeGreaterThan,
      } = groupFilter;

      if (!hasGroupNameFilter && !hasMemberCountFilter) {
        return null;
      }

      const operatorLabels: Record<string, string> = {
        equals: 'equals',
        greaterThan: 'greater than',
        lessThan: 'less than',
        greaterThanOrEqual: 'greater than or equal to',
        lessThanOrEqual: 'less than or equal to',
      };

      const data: Record<string, React.ReactNode> = {};

      if (hasGroupNameFilter) {
        if ((groupNameMode ?? 'name') === 'regex') {
          data.regularExpression = groupNameRegex || 'N/A';
        } else {
          data.groupName = groupName || 'N/A';
          data.exactMatch = groupNameExact ? 'Yes' : 'No';
        }
      }

      if (hasMemberCountFilter) {
        if (memberCountMode === 'range') {
          data.memberCount = `Less than ${
            memberCountRangeLessThan ?? 0
          } or greater than ${memberCountRangeGreaterThan ?? 0}`;
        } else {
          const opLabel =
            operatorLabels[memberCountOperator ?? 'equals'] ??
            memberCountOperator ??
            '';
          data.memberCount = `${capitalize(opLabel)} ${memberCount ?? 0}`;
        }
      }

      return data;
    }, [selectedRule]);

  const personalCalendarFiltersData: Record<string, React.ReactNode> | null =
    useMemo(() => {
      const filter = selectedRule.personalCalendarFilter;
      if (!filter || !filter.hasPersonalCalendarFilter) {
        return null;
      }

      const sharingOptions: React.ReactNode[] = [];

      if (filter.sharedToUserExternalToDomain) {
        sharingOptions.push('User external to domain');
      }
      if (filter.sharedToUserInternalToDomain) {
        sharingOptions.push('User internal to domain');
      }

      const specificUsers = (filter.sharedToSpecificUsers || []).map((user) => (
        <AvatarChip key={user} email={user} type="User" size="small" />
      ));

      const specificGroups = (filter.sharedToGroups || []).map((group) => (
        <AvatarChip key={group} email={group} type="Group" size="small" />
      ));

      const allFilters = [
        ...sharingOptions,
        ...specificUsers,
        ...specificGroups,
      ];

      if (allFilters.length === 0) {
        return null;
      }

      return {
        'Shared To': allFilters,
      };
    }, [selectedRule]);

  return (
    <>
      <ModelDetailsDisplay title="Basic Information" data={basicInfoData} />
      {adminFiltersData && (
        <ModelDetailsDisplay title="Admin Filters" data={adminFiltersData} />
      )}
      {driveFiltersData && (
        <ModelDetailsDisplay title="Drive Filters" data={driveFiltersData} />
      )}
      {groupFiltersData && (
        <ModelDetailsDisplay title="Group Filters" data={groupFiltersData} />
      )}
      {personalCalendarFiltersData && (
        <ModelDetailsDisplay
          title="Personal Calendar Filters"
          data={personalCalendarFiltersData}
        />
      )}
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 'bold', mb: 1, fontSize: 14 }}
        >
          Actions
        </Typography>
        {reviewStepText && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {reviewStepText}
          </Alert>
        )}
        {selectedRule.actionInfo?.actionTodo?.length > 0 ? (
          selectedRule.actionInfo.actionTodo.map((action) => {
            const actionOption = actionOptions.find((x) => x.value === action);
            if (!actionOption) return null;
            return (
              <SharedActionAccordion
                key={action}
                id={action}
                title={actionOption.displayValue}
                description={createDetailSummary(action)}
                type={actionOption.type}
                expanded={expandedAccordions.has(action)}
                onToggleExpand={() => toggleAccordion(action)}
              >
                {createDetailBody(action)}
              </SharedActionAccordion>
            );
          })
        ) : (
          <EmptyStateMessage
            title="No actions recorded yet"
            message="The rule is currently a Draft. Once you configure your actions, the information will appear here."
          />
        )}
      </Box>
    </>
  );
}
